import * as React from 'react';

import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from '../button/button';
import {CopyButton} from '../button/copy-button/copy-button';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from '../icon/svg/upgrade-arrow-icon';
import {Link} from '../link/link';
import type {SubdomainOption} from '../subdomain-select/subdomain-select';
import {SubdomainSlugField} from '../subdomain-slug-field/subdomain-slug-field';
import {Body} from '../typography/body';

import type {UpdatePageConfigInput} from './types';

import styles from './url-customizer.module.css';

type Props = {
    meetingPageUrl: string;
    domain: SubdomainOption;
    slug: string;
    allowedToCustomizeUrl: boolean;
    hasSchedulerFullAccess: boolean;
    subdomainOptions: SubdomainOption[];
    refetchMeetings: () => Promise<any>;
    onUpdatePageConfig: (input: UpdatePageConfigInput) => Promise<any>;
    onOpenFeatureGateModal: () => void;
};

export function UrlCustomizer(props: Props) {
    const [domain, setDomain] = React.useState<SubdomainOption>(props.domain);
    const [slug, setSlug] = React.useState(props.slug);
    const [isCustomizing, setIsCustomizing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const copyableLinkRef = React.useRef(null);
    const idRef = React.useRef<string>(uniqueId('input_'));

    const handleSave = () => {
        setErrorMessage(null);

        props
            .onUpdatePageConfig({
                fromDomainId: domain.value,
                userSlug: slug,
            })
            .then(() => {
                setIsCustomizing(false);
                props.refetchMeetings();
            })
            .catch((error) => {
                const newErrorMessage = safelyGetGraphQLErrorMessage(error);
                if (newErrorMessage) {
                    setErrorMessage(newErrorMessage);
                }
            });
    };

    const handleCancel = () => {
        setIsCustomizing(false);
        setErrorMessage(null);
        setSlug(props.slug);
        setDomain(props.domain);
    };

    const urlStyleName = classnames(styles.url, {
        [styles.truncate]: props.allowedToCustomizeUrl,
    });

    return (
        <>
            <div className={styles['link-section']}>
                <div className={styles['customize-link']}>
                    <input
                        id={idRef.current}
                        name='link'
                        type='text'
                        ref={copyableLinkRef}
                        value={props.meetingPageUrl}
                        onChange={() => {}}
                        hidden={true}
                    />
                    <div className={urlStyleName}>
                        <Link
                            href={props.meetingPageUrl}
                            newTab={true}
                            size='small'
                            variant='primary'
                            shouldTruncate={props.allowedToCustomizeUrl}
                            // @ts-expect-error
                            title={props.meetingPageUrl}
                        >
                            {props.meetingPageUrl}
                        </Link>
                    </div>
                    {props.allowedToCustomizeUrl && (
                        <Button
                            icon={!props.hasSchedulerFullAccess ? UpgradeArrowIcon : undefined}
                            onClick={() => {
                                if (props.hasSchedulerFullAccess) {
                                    setIsCustomizing(true);
                                } else {
                                    props.onOpenFeatureGateModal();
                                }
                            }}
                            variant='upgrade'
                        >
                            Customize
                        </Button>
                    )}
                </div>
                <CopyButton variant='primary' size='big' target={copyableLinkRef} />
            </div>
            {isCustomizing && (
                <div className='flex flex-dir-col gap-8 mb-8'>
                    <Body isBold={true}>Customize this URL</Body>
                    <Body size='small'>
                        Awesome! Let’s customize your URL. Pick a unique URL below that all of your
                        meetings will start with.
                    </Body>
                    <div className='flex gap-16'>
                        <div className={styles['pathname-field']}>
                            <SubdomainSlugField
                                subdomainOptions={props.subdomainOptions}
                                selectedSubdomain={domain}
                                slug={slug}
                                onSlugChange={setSlug}
                                onSelectedSubdomainChange={setDomain}
                                errorMessage={errorMessage}
                            />
                        </div>
                        <div className='flex flex-dir-col'>
                            <Button
                                variant='text-primary'
                                size='small'
                                noPadding={true}
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                            <Button
                                variant='text-secondary'
                                size='small'
                                noPadding={true}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
